import { useState } from "react";
import { AlertCircle, Mail, Shield, Sparkles, X } from "lucide-react";

// Generic free-mail providers — when the user types one of these AND we know
// the scanned domain, we softly nudge them toward a domain-matching email
// (iFocus does this hard; we keep it as a soft warning so we don't lose the
// lead, but signal "real biz emails get prioritised").
const FREE_MAIL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "yahoo.sk", "yahoo.cz", "outlook.com", "hotmail.com",
  "hotmail.sk", "hotmail.cz", "icloud.com", "me.com", "live.com", "live.sk",
  "centrum.sk", "centrum.cz", "azet.sk", "post.sk", "atlas.sk", "zoznam.sk",
  "seznam.cz", "email.cz", "volny.cz", "proton.me", "protonmail.com",
]);

function extractDomain(url) {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "").toLowerCase();
  } catch { return null; }
}

export const EmailCaptureModal = ({ isOpen, onClose, onSubmit, t, isLoading, targetUrl }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [consent, setConsent] = useState(false);

  const scannedDomain = extractDomain(targetUrl);

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    setError('');
    // Soft warning — non-blocking. Triggers only when the user typed a
    // recognisable email + we know the scanned domain.
    const at = v.indexOf("@");
    if (at > 0 && scannedDomain) {
      const emailDomain = v.slice(at + 1).toLowerCase().trim();
      if (emailDomain && emailDomain.includes(".") && !FREE_MAIL_DOMAINS.has(emailDomain)
          && emailDomain !== scannedDomain
          && !emailDomain.endsWith(`.${scannedDomain}`)
          && !scannedDomain.endsWith(`.${emailDomain}`)) {
        setWarning(t.email_domain_mismatch || `Tip: použi email z domény @${scannedDomain} pre prednostné spracovanie`);
        return;
      }
    }
    setWarning('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes('@') || !email.includes('.')) {
      setError(t.email_invalid);
      return;
    }
    setError('');
    onSubmit(email, consent);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-[var(--color-bg)]/50 flex items-center justify-center z-50 p-4" data-testid="email-modal">
      <div className="bg-[var(--color-surface)] max-w-md w-full p-8 relative animate-fade-in">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-[var(--color-text-muted)]"
          data-testid="email-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#a855f7]" />
          </div>
          <h2 className="text-2xl font-black font-['Chivo'] mb-2">{t.email_required}</h2>
          <p className="text-[var(--color-text-muted)]">{t.email_subtitle}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder={scannedDomain ? `tvoj-email@${scannedDomain}` : t.email_placeholder}
              className="w-full px-4 py-3 border-2 border-[var(--color-border)] bg-[var(--color-bg)] text-white placeholder-zinc-500 focus:border-blue-600 focus:outline-none transition-colors"
              data-testid="email-input"
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {error}
              </p>
            )}
            {!error && warning && (
              <p className="text-amber-500 text-xs mt-1.5 flex items-start gap-1" data-testid="email-domain-warning">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{warning}</span>
              </p>
            )}
          </div>
          
          <label className="flex items-start gap-2 text-xs text-[var(--color-text-muted)] cursor-pointer text-left" data-testid="email-marketing-consent-label">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#a855f7] flex-shrink-0" data-testid="email-marketing-consent" />
            <span>{t.email_marketing_consent || "Chcem dostávať ponuky TopRankPulse emailom (max. 5 do konca roka, odhlásenie jedným klikom)."}</span>
          </label>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#a855f7] text-white py-3 font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="email-submit"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                {t.email_submit}
              </>
            )}
          </button>
        </form>
        
        <p className="text-center text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          {t.email_privacy}
        </p>
      </div>
    </div>
  );
};
